// Pure draft-mutation actions for the program editor.
//
// Companion to programEdit.ts (the matrix model + pivots). These are the
// immutable `(draft, …args) => Draft` operations the editor UI binds to
// its buttons/inputs. Lifting them out of the React component gave the
// legacy web editor and the shared @heraion/ui editor one tested core, and makes the non-trivial ones (seed-from-last, kind
// re-seed, move bounds, group derivation) unit-testable without a UI.
// Each returns the input draft unchanged when the target index is out of
// range — callers never have to guard. Keep free of React / UI imports.

import {
  blankCell,
  blankSetRow,
  blankSlot,
  kindAt,
  seedForIntensityKind,
  VALUELESS_KINDS,
  type Draft,
  type IntensityKind,
  type SessionDraft,
  type SetRowDraft,
  type SlotDraft,
  type CellDraft,
} from "./programEdit.js";
import { addSetEveryRound, removeSetEveryRound } from "./programEditRoundSets.js";
import { hasActiveWeek } from "./programBlocks.js";
import { uuidv4 } from "./uuid.js";
import { mapRoundTypes, resizeRoundTypes } from "./programRoundTypes.js";

export const updateSession = (
  draft: Draft,
  idx: number,
  patch: Partial<SessionDraft>,
): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
});

// PE-11: structural edits of a program already made: these grow/shrink
// its day count and cycle length in place. Schema bounds: sessions
// 1..7, cycleWeeks 1..12. New sessions and slots carry a client-minted uuid;
// the server keeps it on save. A fresh day starts empty; the save guard
// ("every day needs an exercise") nudges the user to fill it.

// Schema bounds (schemas/program.ts) exported so the editor UI clamps against
// the same numbers instead of re-typing 7 / 1 / 12 at every stepper + cap.
export const MAX_SESSIONS = 7;
export const MIN_CYCLE_WEEKS = 1;
export const MAX_CYCLE_WEEKS = 12;

/** Adding a training day implies at least that many sessions/week; keep the
 *  higher of the two (sessionsPerWeek stays independent otherwise). */
export const sessionsPerWeekAfterAdd = (draft: Draft): number =>
  Math.min(MAX_SESSIONS, Math.max(draft.sessionsPerWeek, draft.sessions.length + 1));

export const addSession = (draft: Draft, name: string): Draft =>
  draft.sessions.length >= MAX_SESSIONS
    ? draft
    : {
        ...draft,
        sessions: [...draft.sessions, { id: uuidv4(), name, notes: "", slots: [] }],
        sessionsPerWeek: sessionsPerWeekAfterAdd(draft),
      };

export const removeSession = (draft: Draft, idx: number): Draft => {
  if (draft.sessions.length <= 1 || idx < 0 || idx >= draft.sessions.length) {
    return draft;
  }
  const nextLen = draft.sessions.length - 1;
  return {
    ...draft,
    sessions: draft.sessions.filter((_, i) => i !== idx),
    sessionsPerWeek: Math.min(draft.sessionsPerWeek, nextLen),
  };
};

// Grow or shrink the cycle. Growing copies the LAST week's cells/notes/inactive
// flag into each new week (Antenore's pick — a new week extends the ramp, then
// you tweak it), so every per-week array (cells, notesPerCell, inactiveWeeks)
// stays exactly cycleWeeks long. Shrinking truncates from the end — and a slot
// active ONLY in the dropped tail (e.g. an accessory run W4–W6, cycle cut to 3)
// would be left with zero active weeks: a phantom that derives to a verbatim
// card and blocks Save ("slot inactive on every week"). We sweep those away —
// dropping the tail weeks drops their exclusive sets, exactly what the shrink
// confirm promises.
/** Make ONE slot fit a cycle of `weeks`: every per-week array grows by copying
 *  its last entry and shrinks by truncation. Growing copies rather than blanks
 *  by DEFAULT because that is what an author meant by "one more week" — the
 *  5/3/1 vertical fill — and it is the behaviour `setCycleWeeks` has always had.
 *  `grow: "blank"` asks for the other thing: an empty week (PE-45).
 *
 *  ⭐ Extracted from `setCycleWeeks` (PE-40): importing a saved workout into a
 *  program needs exactly this, because `planToDraft` builds its slots at
 *  `cycleWeeks: 1` and dropping them into an N-week day unresized leaves every
 *  reader indexing past the end of `cells`. Two real callers, so the primitive
 *  is built rather than copied. */
export const resizeSlotToWeeks = (
  slot: SlotDraft,
  weeks: number,
  // PE-45 — what a GROWN week inherits. "clone" is the historical behaviour and
  // stays the default, so no existing caller changes by being read differently.
  // "blank" is the new opt-in: the added weeks are EMPTY, meaning this slot does
  // not run in them at all. Antenore's own answer to "cosa vuol dire vuota"
  // (30/08): a column with no exercises placed, which the matrix draws empty and
  // a hold on a cell fills — not the same exercises with the numbers rubbed out.
  // ⛔ Opt-in and not a flipped default: this is a shared chokepoint, and its two
  // callers want opposite things [[feedback_a_migration_inside_a_shared_chokepoint_needs_opt_in]].
  grow: "clone" | "blank" = "clone",
): SlotDraft => {
  const resize = <T,>(arr: T[], fill: (last: T | undefined) => T): T[] =>
    weeks <= arr.length
      ? arr.slice(0, weeks)
      : [
          ...arr,
          ...Array.from({ length: weeks - arr.length }, () =>
            fill(arr[arr.length - 1]),
          ),
        ];
  const blank = grow === "blank";
  return {
    ...slot,
    // An empty week is one the slot does not RUN in: inactive, not merely
    // blank-celled. The cells below are reset too so that placing the exercise
    // there later starts from a clean cell instead of a stale copy.
    inactiveWeeks: resize(slot.inactiveWeeks, (last) => (blank ? true : (last ?? false))),
    setRows: slot.setRows.map((r) => ({
      ...r,
      cells: resize(r.cells, (last) => (blank ? blankCell() : { ...(last ?? blankCell()) })),
      notesPerCell: resize(r.notesPerCell, (last) => (blank ? "" : (last ?? ""))),
      // Stays aligned with notesPerCell when present: a week added
      // to the cycle has no stored translation behind it yet.
      ...(r.notesRawPerCell
        ? { notesRawPerCell: resize(r.notesRawPerCell, () => undefined) }
        : {}),
    })),
  };
};

export const setCycleWeeks = (
  draft: Draft,
  weeks: number,
  // PE-45 — see resizeSlotToWeeks. Default "clone" keeps every existing caller
  // exactly where it was; the editor's `W ＋` tap passes "blank".
  grow: "clone" | "blank" = "clone",
): Draft => {
  const next = Math.max(
    MIN_CYCLE_WEEKS,
    Math.min(MAX_CYCLE_WEEKS, Math.floor(weeks)),
  );
  if (next === draft.cycleWeeks) return draft;
  return {
    // #336 S2: a round grown by "clone" takes the type of the round it
    // copies (the last); a "blank" one is normal.
    ...mapRoundTypes(draft, (types) =>
      resizeRoundTypes(types, next, grow === "clone" ? types[types.length - 1] : undefined),
    ),
    cycleWeeks: next,
    sessions: draft.sessions.map((s) => ({
      ...s,
      // ⛔ The filter is SHRINK-only business and stays here, not in the
      // per-slot resize: a slot whose every week just went inactive has no
      // reason to exist, but that is a fact about resizing the CYCLE, not
      // about fitting one slot to it.
      slots: s.slots
        .map((sl) => resizeSlotToWeeks(sl, next, grow))
        .filter(hasActiveWeek),
    })),
  };
};

/** Append already-formed slots to a day. The slots must already fit the
 *  draft's cycle (see `resizeSlotToWeeks`) — this does not reshape them, so a
 *  caller that skips the resize gets exactly the ragged arrays it deserves. */
export const appendSlots = (
  draft: Draft,
  sIdx: number,
  slots: SlotDraft[],
): Draft => {
  const session = draft.sessions[sIdx];
  if (!session || slots.length === 0) return draft;
  return {
    ...draft,
    sessions: draft.sessions.map((s, i) =>
      i === sIdx ? { ...s, slots: [...s.slots, ...slots] } : s,
    ),
  };
};

export const updateSlot = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  patch: Partial<SlotDraft>,
): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) => {
    if (i !== sIdx) return s;
    return {
      ...s,
      slots: s.slots.map((sl, j) =>
        j === slotIdx ? { ...sl, ...patch } : sl,
      ),
    };
  }),
});

export const addSlot = (draft: Draft, sIdx: number): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) =>
    i === sIdx
      ? { ...s, slots: [...s.slots, blankSlot(draft.cycleWeeks)] }
      : s,
  ),
});

export const removeSlot = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) =>
    i === sIdx
      ? { ...s, slots: s.slots.filter((_, j) => j !== slotIdx) }
      : s,
  ),
});

// moveSlot lives in ./programEditActionsGroup: it moves a superset as one.

// Internal: the shared row patcher updateCell and setRowKind both go
// through. Not exported — every editor reaches rows via those two, or via
// the composer's own same-named useCallback, and an `export *` barrel makes
// an internal helper look like public API to anyone grepping for it.
const updateRow = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  rowIdx: number,
  patch: Partial<SetRowDraft>,
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  if (!slot) return draft;
  return updateSlot(draft, sIdx, slotIdx, {
    setRows: slot.setRows.map((r, i) => (i === rowIdx ? { ...r, ...patch } : r)),
  });
};

export const updateCell = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  rowIdx: number,
  weekIdx: number,
  patch: Partial<CellDraft>,
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  const row = slot?.setRows[rowIdx];
  if (!slot || !row) return draft;
  return updateRow(draft, sIdx, slotIdx, rowIdx, {
    cells: row.cells.map((c, i) => (i === weekIdx ? { ...c, ...patch } : c)),
  });
};

export const addSetRow = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  if (!slot) return draft;
  // Seed the new row from the last existing one so adding a back-off set
  // after a top-set inherits a sensible starting point.
  // #336 S1: every round gets one more, after its own last set.
  if (slot.setRows.length > 0) return updateSlot(draft, sIdx, slotIdx, addSetEveryRound(slot));
  return updateSlot(draft, sIdx, slotIdx, { setRows: [blankSetRow(draft.cycleWeeks)] });
};

export const removeSetRow = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  rowIdx: number,
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  if (!slot || slot.setRows.length <= 1) return draft;
  // #336 S1: in every round; a round whose only set it was keeps it.
  return updateSlot(draft, sIdx, slotIdx, removeSetEveryRound(slot, rowIdx));
};

// PE-55: a value's SCALE is tied to its kind: an RPE 8 and a %MAX 8 are
// both the string "8" but mean an order of magnitude apart. So a kind
// change can never carry the old number along, whatever kind it was; the
// only safe reset is the new kind's own seed (or blank, for valueless
// kinds). Re-tapping the SAME kind is not a change, and leaves the typed
// value alone. Shared by the program editor's setRowKind below and the
// composer's setMemberKind (useComposerFormMutations.ts): one bug fixed
// twice was the sign two callsites had drifted from a single rule.
export const intensityValueAfterKindChange = (
  prevKind: IntensityKind,
  nextKind: IntensityKind,
  prevValue: string,
): string =>
  prevKind === nextKind
    ? prevValue
    : VALUELESS_KINDS.has(nextKind)
      ? ""
      : seedForIntensityKind(nextKind);

// `week`: the round the tap came from. Re-tapping the kind that round
// already shows is no change (PE-55, #336): a kg row with RIR in W4 keeps
// both when RIR is tapped in W4 or kg in W1.
export const setRowKind = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  rowIdx: number,
  kind: IntensityKind,
  week?: number,
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  const row = slot?.setRows[rowIdx];
  if (!slot || !row) return draft;
  if ((week === undefined ? row.intensityKind : kindAt(row, week)) === kind) return draft;
  return updateRow(draft, sIdx, slotIdx, rowIdx, {
    intensityKind: kind,
    // The whole row moves to `kind`, rounds with their own axis too (#336).
    cells: row.cells.map(({ kind: own, ...c }) => ({
      ...c,
      intensityValue: intensityValueAfterKindChange(
        own ?? row.intensityKind,
        kind,
        c.intensityValue,
      ),
    })),
  });
};

// PE-22 K3.3 — the reps⇄time BOUND, on the ROW (workKind lives beside
// intensityKind, same split as SetRowDraft's own doc comment). No dedicated
// setter existed before this: HI-1's own composer (ComposerSetRow's BOUNDS
// chips) patches the block-form row directly, and the tests that pin the
// axis (hiitTimeAxis.test.ts) write `row.workKind` straight onto a Draft in
// test code. `undefined` clears the bound — a reps set, exactly like
// addSetRow's blank seed.
export const setWorkKind = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  rowIdx: number,
  kind: SetRowDraft["workKind"],
): Draft => {
  const slot = draft.sessions[sIdx]?.slots[slotIdx];
  if (!slot?.setRows[rowIdx]) return draft;
  return updateRow(draft, sIdx, slotIdx, rowIdx, { workKind: kind });
};

// Assignee chip-row actions. addAssigneeEmail trims + lowercases + dedupes
// + loose-validates; an invalid or duplicate value returns the draft
// unchanged (the UI clears its input field regardless). Same rules as the
// legacy editor's addEmailFromDraft.
export const addAssigneeEmail = (draft: Draft, raw: string): Draft => {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return draft;
  if (draft.assigneeEmails.includes(trimmed)) return draft;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return draft;
  return { ...draft, assigneeEmails: [...draft.assigneeEmails, trimmed] };
};

export const removeAssigneeEmail = (draft: Draft, email: string): Draft => ({
  ...draft,
  assigneeEmails: draft.assigneeEmails.filter((e) => e !== email),
});

export const removeAssignee = (draft: Draft, sub: string): Draft => ({
  ...draft,
  assignedTo: draft.assignedTo.filter((s) => s !== sub),
});
